import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API });

http.interceptors.request.use((config) => {
  const token = localStorage.getItem("wt_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const setToken = (t) => (t ? localStorage.setItem("wt_token", t) : localStorage.removeItem("wt_token"));
export const getToken = () => localStorage.getItem("wt_token");

export const analyzeWebsite = (url) => http.post("/analyze", { url }).then((r) => r.data);
export const getAudit = (id) => http.get(`/audit/${id}`).then((r) => r.data);
export const getTransformationPlan = (audit_id) =>
  http.post("/transformation-plan", { audit_id }).then((r) => r.data);
export const runPlanner = (payload) => http.post("/planner", payload).then((r) => r.data);
export const createLead = (payload) => http.post("/leads", payload).then((r) => r.data);
export const createConsultation = (payload) =>
  http.post("/consultations", payload).then((r) => r.data);
export const getAdminStats = () => http.get("/admin/stats").then((r) => r.data);
export const getAdminAudits = () => http.get("/admin/audits").then((r) => r.data);

// Lead Management / CRM (admin)
export const crmLeads = (params = {}) => http.get("/admin/leads", { params }).then((r) => r.data);
export const crmUpdateLead = (payload) => http.patch("/admin/leads", payload).then((r) => r.data);
export const adminAnalytics = (rng = 30) => http.get(`/admin/analytics?rng=${rng}`).then((r) => r.data);
export const adminAnalyticsAsk = (question, rng = 30) => http.post("/admin/analytics/ask", { question, rng }).then((r) => r.data);
export const adminAnalyticsSuggestions = () => http.get("/admin/analytics/suggestions").then((r) => r.data);
export const getReportSettings = () => http.get("/admin/reports/settings").then((r) => r.data);
export const setReportSettings = (payload) => http.post("/admin/reports/settings", payload).then((r) => r.data);
export const sendReportNow = () => http.post("/admin/reports/send-now").then((r) => r.data);
export const adminClients = () => http.get("/admin/clients").then((r) => r.data);
export const createClient = (payload) => http.post("/admin/clients", payload).then((r) => r.data);
export const deleteClient = (id) => http.delete(`/admin/clients/${id}`).then((r) => r.data);
export const clientDashboard = (token) => http.get(`/public/client-dashboard/${token}`).then((r) => r.data);
export const portalDashboardLink = () => http.get("/portal/dashboard-link").then((r) => r.data);

// Auth
export const apiLogin = (email, password) => http.post("/auth/login", { email, password }).then((r) => r.data);
export const apiRegister = (payload) => http.post("/auth/register", payload).then((r) => r.data);
export const apiMe = () => http.get("/auth/me").then((r) => r.data);

// Portal
export const getOverview = () => http.get("/portal/overview").then((r) => r.data);
export const getWebsites = () => http.get("/portal/websites").then((r) => r.data);
export const getProjects = () => http.get("/portal/projects").then((r) => r.data);
export const getPortalLeads = () => http.get("/portal/leads").then((r) => r.data);
export const getAnalytics = (rng = 30) => http.get(`/portal/analytics?rng=${rng}`).then((r) => r.data);
export const getIntegrations = () => http.get("/portal/integrations").then((r) => r.data);
export const connectIntegration = (key, connected) => http.post("/portal/integrations/connect", { key, connected }).then((r) => r.data);

// Health + rebuild
export const runHealthCheck = (url) => http.post("/health-check", { url }).then((r) => r.data);
export const runRebuildPreview = (url) => http.post("/rebuild-preview", { url }).then((r) => r.data);
export const pdfUrl = (path) => `${API}${path.replace("/api", "")}`;
export const unlockRebuild = (id, payload) => http.post(`/rebuild-preview/${id}/unlock`, payload).then((r) => r.data);
export const rebuildPdfBlob = (id, { token, email } = {}) =>
  http.get(`/rebuild-preview/${id}/pdf`, {
    params: { ...(token && { token }), ...(email && { email }) }, responseType: "blob",
  }).then((r) => URL.createObjectURL(r.data));

// Redesign gallery
export const getGallery = () => http.get("/gallery").then((r) => r.data);
export const getRedesigns = () => http.get("/portal/redesigns").then((r) => r.data);

// Self-serve chatbots (portal)
export const getBots = () => http.get("/portal/bots").then((r) => r.data);
export const createBot = (payload) => http.post("/portal/bots", payload).then((r) => r.data);
export const updateBot = (id, payload) => http.patch(`/portal/bots/${id}`, payload).then((r) => r.data);
export const deleteBot = (id) => http.delete(`/portal/bots/${id}`).then((r) => r.data);
export const ingestBot = (id, url) => http.post(`/portal/bots/${id}/ingest`, { url }).then((r) => r.data);
export const autoConfigBot = (url) => http.post("/portal/bots/auto-config", { url }).then((r) => r.data);
export const botInsights = (id) => http.get(`/portal/bots/${id}/insights`).then((r) => r.data);
export const getPlanUsage = () => http.get("/portal/plan").then((r) => r.data);

// Customer Dashboard v1
export const portalConversations = () => http.get("/portal/conversations").then((r) => r.data);
export const portalConversationDetail = (sid) => http.get(`/portal/conversations/${sid}`).then((r) => r.data);
export const portalUnanswered = () => http.get("/portal/unanswered").then((r) => r.data);
export const portalInvoices = () => http.get("/portal/invoices").then((r) => r.data);
export const portalExport = () => http.get("/portal/export").then((r) => r.data);
export const portalDataDelete = (payload) => http.post("/portal/data-delete", payload).then((r) => r.data);

// Country platform (India instance + admin)
export const getCountry = (code) => http.get(`/country/${code}`).then((r) => r.data);
export const getCountryPackages = (code) => http.get(`/country/${code}/packages`).then((r) => r.data);
export const submitCountryQuote = (code, payload) => http.post(`/country/${code}/quote`, payload).then((r) => r.data);
export const adminCountryPackages = (code) => http.get(`/admin/country/${code}/packages`).then((r) => r.data);
export const adminCreatePackage = (code, payload) => http.post(`/admin/country/${code}/packages`, payload).then((r) => r.data);
export const adminUpdatePackage = (code, id, payload) => http.patch(`/admin/country/${code}/packages/${id}`, payload).then((r) => r.data);
export const adminDeletePackage = (code, id) => http.delete(`/admin/country/${code}/packages/${id}`).then((r) => r.data);
export const adminCountryLeads = (code) => http.get(`/admin/country/${code}/leads`).then((r) => r.data);
export const countryOpportunities = (code, payload) => http.post(`/country/${code}/opportunities`, payload).then((r) => r.data);
export const countryGeoState = (code, state) => http.get(`/country/${code}/geo/${state}`).then((r) => r.data);
export const countryGeoCity = (code, state, city) => http.get(`/country/${code}/geo/${state}/${city}`).then((r) => r.data);
export const resolveBot = (orgId) => http.get(`/ai/resolve/${orgId}`).then((r) => r.data);
export const publicBotChat = (key, payload) => http.post(`/ai/public/${key}/chat`, payload).then((r) => r.data);

// Tenant platform (Kerala.com base tenant + its CMS/CRM/packages)
export const getTenants = () => http.get("/tenants").then((r) => r.data);
export const getTenant = (tid) => http.get(`/tenant/${tid}`).then((r) => r.data);
export const getTenantPackages = (tid) => http.get(`/tenant/${tid}/packages`).then((r) => r.data);
export const submitTenantLead = (tid, payload) => http.post(`/tenant/${tid}/lead`, payload).then((r) => r.data);
export const adminTenantPackages = (tid) => http.get(`/admin/tenant/${tid}/packages`).then((r) => r.data);
export const adminCreateTenantPackage = (tid, payload) => http.post(`/admin/tenant/${tid}/packages`, payload).then((r) => r.data);
export const adminUpdateTenantPackage = (tid, id, payload) => http.patch(`/admin/tenant/${tid}/packages/${id}`, payload).then((r) => r.data);
export const adminDeleteTenantPackage = (tid, id) => http.delete(`/admin/tenant/${tid}/packages/${id}`).then((r) => r.data);
export const adminTenantLeads = (tid) => http.get(`/admin/tenant/${tid}/leads`).then((r) => r.data);
export const adminUpdateTenantLead = (tid, id, payload) => http.patch(`/admin/tenant/${tid}/leads/${id}`, payload).then((r) => r.data);

// Editors (admin-assigned by email)
export const adminEditors = () => http.get("/admin/editors").then((r) => r.data);
export const createEditor = (payload) => http.post("/admin/editors", payload).then((r) => r.data);
export const deleteEditor = (id) => http.delete(`/admin/editors/${id}`).then((r) => r.data);

// Public Audit — emailed branded PDF report
export const auditEmailReport = (payload) => http.post("/audit/email-report", payload).then((r) => r.data);

// India customer dashboard
export const indiaBilling = () => http.get("/portal/india/billing").then((r) => r.data);

// Audit Enquiries (admin — everyone who tested a website)
export const adminAuditEnquiries = (q = "", source = "") =>
  http.get("/admin/audit-enquiries", { params: { q, source } }).then((r) => r.data);
export const deleteAuditEnquiry = (id) => http.delete(`/admin/audit-enquiries/${id}`).then((r) => r.data);
export const convertEnquiryToLead = (id, payload) => http.post(`/admin/audit-enquiries/${id}/convert-lead`, payload).then((r) => r.data);
export const getAutoDeckSettings = () => http.get("/admin/auto-deck/settings").then((r) => r.data);
export const setAutoDeckSettings = (payload) => http.post("/admin/auto-deck/settings", payload).then((r) => r.data);

// AI Growth Advisor (public) — recommends the right lead-gen services for a business
export const growthAdvisorRecommend = (payload) => http.post("/growth-advisor/recommend", payload).then((r) => r.data);
export const growthAdvisorProposal = (payload) => http.post("/growth-advisor/proposal", payload).then((r) => r.data);

// Full Site Redesign Decks (admin — AI redesign + copy → combined PDF proposal)
export const redesignDiscover = (url) => http.post("/admin/redesign/discover", { url }).then((r) => r.data);
export const redesignGenerate = (payload) => http.post("/admin/redesign/generate", payload).then((r) => r.data);
export const redesignJobs = () => http.get("/admin/redesign/jobs").then((r) => r.data);
export const redesignJob = (id) => http.get(`/admin/redesign/jobs/${id}`).then((r) => r.data);
export const redesignDelete = (id) => http.delete(`/admin/redesign/jobs/${id}`).then((r) => r.data);
export const redesignLink = (id) => http.get(`/admin/redesign/jobs/${id}/link`).then((r) => r.data);
export const redesignEmail = (id) => http.post(`/admin/redesign/jobs/${id}/email`).then((r) => r.data);

// Kerala gallery + City Partner network
export const getTenantGallery = (tid) => http.get(`/tenant/${tid}/gallery`).then((r) => r.data);
export const applyPartner = (tid, payload) => http.post(`/tenant/${tid}/partner-apply`, payload).then((r) => r.data);
export const adminPartners = (tid) => http.get(`/admin/tenant/${tid}/partners`).then((r) => r.data);
export const adminPartnerStatus = (tid, id, payload) => http.patch(`/admin/tenant/${tid}/partners/${id}`, payload).then((r) => r.data);
export const adminPartnerDelete = (tid, id) => http.delete(`/admin/tenant/${tid}/partners/${id}`).then((r) => r.data);
export const partnerMe = () => http.get("/partner/me").then((r) => r.data);
export const partnerUpdateLead = (id, payload) => http.patch(`/partner/leads/${id}`, payload).then((r) => r.data);

// Razorpay (India INR payments + GST invoices)
export const rzpCreateOrder = (payload) => http.post("/razorpay/order", payload).then((r) => r.data);
export const rzpVerify = (payload) => http.post("/razorpay/verify", payload).then((r) => r.data);
export const rzpSubscribe = (payload) => http.post("/razorpay/subscribe", payload).then((r) => r.data);
export const rzpSubscriptionVerify = (payload) => http.post("/razorpay/subscription/verify", payload).then((r) => r.data);
export const indiaInvoicePdfUrl = async (id) => {
  const res = await http.get(`/portal/india/invoices/${id}/pdf`, { responseType: "blob" });
  return URL.createObjectURL(res.data);
};
export const loadRazorpayCheckout = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

// Payments (Stripe)
export const createCheckout = (lookup_key) => http.post("/payments/checkout", { lookup_key, origin_url: window.location.origin }).then((r) => r.data);
export const paymentStatus = (sessionId) => http.get(`/payments/status/${sessionId}`).then((r) => r.data);
export const adminGallery = () => http.get("/admin/gallery").then((r) => r.data);
export const adminFeatureGallery = (id, featured) => http.patch("/admin/gallery", { id, featured }).then((r) => r.data);
export const getConcept = (id) => http.get(`/concept/${id}`).then((r) => r.data);

// Outreach messages + action tracker
export const conceptOutreach = (conceptId, origin) => http.get(`/admin/concepts/${conceptId}/outreach`, { params: { origin } }).then((r) => r.data);
export const logOutreach = (payload) => http.post("/admin/leads/outreach", payload).then((r) => r.data);
export const updateOutreach = (payload) => http.patch("/admin/leads/outreach", payload).then((r) => r.data);

// CMS
export const cmsList = (tenant) => http.get("/cms/pages", { params: tenant ? { tenant } : {} }).then((r) => r.data);
export const cmsGet = (slug, tenant) => http.get(`/cms/pages/${slug}`, { params: tenant ? { tenant } : {} }).then((r) => r.data);
export const cmsCreate = (payload) => http.post("/cms/pages", payload).then((r) => r.data);
export const cmsUpdate = (id, payload) => http.put(`/cms/pages/${id}`, payload).then((r) => r.data);
export const cmsDelete = (id) => http.delete(`/cms/pages/${id}`).then((r) => r.data);
export const cmsUnfurl = (url) => http.post("/cms/unfurl", { url }).then((r) => r.data);

// SocialPulse SSO (OIDC)
export const oidcConfig = () => http.get("/auth/oidc/config").then((r) => r.data);
export const oidcCallback = (payload) => http.post("/auth/oidc/callback", payload).then((r) => r.data);

// AI Agent Platform (admin)
export const aiListAgents = () => http.get("/ai/agents").then((r) => r.data);
export const aiGetAgent = (id) => http.get(`/ai/agents/${id}`).then((r) => r.data);
export const aiSaveAgent = (payload) => http.post("/ai/agents", payload).then((r) => r.data);
export const aiDeleteAgent = (id) => http.delete(`/ai/agents/${id}`).then((r) => r.data);
export const aiIngest = (id, url) => http.post(`/ai/agents/${id}/ingest`, { url }).then((r) => r.data);
export const aiAutoConfig = (url) => http.post("/ai/auto-config", { url }).then((r) => r.data);
export const aiInsights = (id) => http.get(`/ai/agents/${id}/insights`).then((r) => r.data);
export const aiAgentPhotos = (id) => http.get(`/ai/agents/${id}/photos`).then((r) => r.data);
// AI Agent Platform (public widget)
export const aiPublicConfig = (pk) => http.get(`/ai/public/${pk}`).then((r) => r.data);
export const aiResolveOrg = (org) => http.get(`/ai/resolve/${org}`).then((r) => r.data);
export const aiPublicChat = (pk, session_id, message, image_base64 = null) => http.post(`/ai/public/${pk}/chat`, { session_id, message, image_base64 }).then((r) => r.data);

// SEO + AI Visibility (Content Intelligence)
export const seoAnalyzePage = (url) => http.post("/seo/analyze-page", { url }).then((r) => r.data);
export const leadGenAudit = (url, source) => http.post("/leadgen/audit", { url, source }).then((r) => r.data);
export const seoFindBestPage = (domain) => http.post("/seo/find-best-page", { domain }).then((r) => r.data);
export const seoRewrite = (url, recommendations = null) => http.post("/seo/rewrite", { url, recommendations }).then((r) => r.data);
export const seoSchema = (url) => http.post("/seo/schema", { url }).then((r) => r.data);
export const seoListSites = () => http.get("/seo/sites").then((r) => r.data);
export const seoCreateSite = (payload) => http.post("/seo/sites", payload).then((r) => r.data);
export const seoDeleteSite = (id) => http.delete(`/seo/sites/${id}`).then((r) => r.data);
export const seoCrawlSite = (id) => http.post(`/seo/sites/${id}/crawl`).then((r) => r.data);
export const seoSitePages = (id) => http.get(`/seo/sites/${id}/pages`).then((r) => r.data);
export const seoSiteDashboard = (id) => http.get(`/seo/sites/${id}/dashboard`).then((r) => r.data);
export const seoPublish = (payload) => http.post("/seo/publish", payload).then((r) => r.data);
export const seoPublishedList = () => http.get("/seo/published").then((r) => r.data);
export const seoPublishedGet = (slug) => http.get(`/seo/published/${slug}`).then((r) => r.data);
export const seoUnpublish = (slug) => http.delete(`/seo/published/${slug}`).then((r) => r.data);
export const seoVersions = (slug) => http.get(`/seo/versions`, { params: { slug } }).then((r) => r.data);
export const seoRollback = (versionId) => http.post(`/seo/versions/${versionId}/rollback`).then((r) => r.data);
export const seoPriorityQueue = (id) => http.get(`/seo/sites/${id}/queue`).then((r) => r.data);
export const seoBulkOptimize = (id, limit = 5) => http.post(`/seo/sites/${id}/bulk-optimize`, { limit }).then((r) => r.data);
export const seoJobStatus = (jobId) => http.get(`/seo/jobs/${jobId}`).then((r) => r.data);
export const seoJobsList = (siteId) => http.get(`/seo/jobs`, { params: { site_id: siteId } }).then((r) => r.data);
export const seoPublishDraft = (versionId) => http.post(`/seo/drafts/${versionId}/publish`).then((r) => r.data);

// SEO Phase 2 — competitor intelligence, hreflang, cannibalization
export const seoRunCompetitors = (siteId, competitors) => http.post(`/seo/sites/${siteId}/competitors`, { competitors }).then((r) => r.data);
export const seoGetCompetitors = (siteId) => http.get(`/seo/sites/${siteId}/competitors`).then((r) => r.data);
export const seoHreflang = (siteId) => http.get(`/seo/sites/${siteId}/hreflang`).then((r) => r.data);
export const seoCannibalization = (siteId) => http.get(`/seo/sites/${siteId}/cannibalization`).then((r) => r.data);

export const scoreTone = (n) => {
  if (n >= 85) return { color: "#16A34A", label: "Excellent" };
  if (n >= 70) return { color: "#2563EB", label: "Good" };
  if (n >= 50) return { color: "#F59E0B", label: "Needs Work" };
  return { color: "#EF4444", label: "Critical" };
};

export const streamChat = async (session_id, message, onDelta, onDone) => {
  const res = await fetch(`${API}/chat/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id, message }),
  });
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop();
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith("data:")) continue;
      try {
        const data = JSON.parse(line.slice(5).trim());
        if (data.delta) onDelta(data.delta);
        if (data.done && onDone) onDone();
      } catch (e) {}
    }
  }
  if (onDone) onDone();
};

// Service Bundles & Pricing (Phase 2a)
export const getPricing = () => http.get("/pricing").then((r) => r.data);
export const adminGetPricing = () => http.get("/admin/pricing").then((r) => r.data);
export const adminSavePricing = (payload) => http.put("/admin/pricing", payload).then((r) => r.data);

// Industry x Location pages (Phase 2a)
export const listIndustryPages = () => http.get("/industry-pages").then((r) => r.data);
export const getIndustryPage = (slug) => http.get(`/industry-pages/${slug}`).then((r) => r.data);
export const adminListIndustryPages = () => http.get("/admin/industry-pages").then((r) => r.data);
export const adminGenerateIndustryPage = (industry, location) => http.post("/admin/industry-pages/generate", { industry, location }).then((r) => r.data);
export const adminPatchIndustryPage = (id, patch) => http.patch(`/admin/industry-pages/${id}`, patch).then((r) => r.data);
export const adminDeleteIndustryPage = (id) => http.delete(`/admin/industry-pages/${id}`).then((r) => r.data);

// Growth Advisor proposals (admin tracking)
export const adminListProposals = () => http.get("/admin/proposals").then((r) => r.data);
export const adminDemoLeads = (demo) => http.get("/admin/demo-leads", { params: demo ? { demo } : {} }).then((r) => r.data);
export const getCatalog = () => http.get("/catalog").then((r) => r.data);
export const adminGetCatalog = () => http.get("/admin/catalog").then((r) => r.data);
export const adminPutCatalog = (payload) => http.put("/admin/catalog", payload).then((r) => r.data);
export const adminResetCatalog = () => http.post("/admin/catalog/reset").then((r) => r.data);
export const getEmbedOptions = () => http.get("/embed/options").then((r) => r.data);
// What's New (admin-editable)
export const getWhatsNew = () => http.get("/whats-new").then((r) => r.data);
export const adminGetNews = () => http.get("/admin/whats-new").then((r) => r.data);
export const adminPutNews = (payload) => http.put("/admin/whats-new", payload).then((r) => r.data);
export const adminResetNews = () => http.post("/admin/whats-new/reset").then((r) => r.data);
// Embed instances
export const adminListInstances = () => http.get("/admin/embed/instances").then((r) => r.data);
export const adminCreateInstance = (payload) => http.post("/admin/embed/instances", payload).then((r) => r.data);
export const adminUpdateInstance = (id, payload) => http.put(`/admin/embed/instances/${id}`, payload).then((r) => r.data);
export const adminDeleteInstance = (id) => http.delete(`/admin/embed/instances/${id}`).then((r) => r.data);
export const adminInstanceLeads = (id) => http.get(`/admin/embed/instances/${id}/leads`).then((r) => r.data);
// Growth Agent work log
export const getGrowthWorklog = () => http.get("/growth-agent/worklog").then((r) => r.data);
export const adminWorklogList = () => http.get("/admin/agent/worklog").then((r) => r.data);
export const adminWorklogAdd = (payload) => http.post("/admin/agent/worklog", payload).then((r) => r.data);
export const adminWorklogUpdate = (id, patch) => http.patch(`/admin/agent/worklog/${id}`, patch).then((r) => r.data);
export const adminWorklogDelete = (id) => http.delete(`/admin/agent/worklog/${id}`).then((r) => r.data);
// AI-Search Visibility
export const getAiVisibility = () => http.get("/ai-visibility").then((r) => r.data);
export const adminAivisList = () => http.get("/admin/ai-visibility").then((r) => r.data);
export const adminAivisAdd = (payload) => http.post("/admin/ai-visibility", payload).then((r) => r.data);
export const adminAivisDelete = (id) => http.delete(`/admin/ai-visibility/${id}`).then((r) => r.data);
export const adminProposalLink = (id) => http.get(`/admin/proposals/${id}/link`).then((r) => r.data);

// Conversion Lab (A/B testing)
export const expAssign = (key, visitor) => http.post(`/experiments/${key}/assign`, { visitor }).then((r) => r.data);
export const expConvert = (key, visitor, variant) => http.post(`/experiments/${key}/convert`, { visitor, variant }).then((r) => r.data);
export const adminExpList = () => http.get("/admin/experiments").then((r) => r.data);
export const adminExpCreate = (payload) => http.post("/admin/experiments", payload).then((r) => r.data);
export const adminExpUpdate = (id, patch) => http.patch(`/admin/experiments/${id}`, patch).then((r) => r.data);
export const adminExpReset = (id) => http.post(`/admin/experiments/${id}/reset`).then((r) => r.data);
export const adminExpPromote = (id, variantId) => http.post(`/admin/experiments/${id}/promote/${variantId}`).then((r) => r.data);
export const adminExpUnpromote = (id) => http.post(`/admin/experiments/${id}/unpromote`).then((r) => r.data);
export const adminExpDelete = (id) => http.delete(`/admin/experiments/${id}`).then((r) => r.data);

// CMS Version History & restore points
export const cmsVersions = (pageId) => http.get(`/cms/pages/${pageId}/versions`).then((r) => r.data);
export const cmsVersionDetail = (pageId, versionId) => http.get(`/cms/pages/${pageId}/versions/${versionId}`).then((r) => r.data);
export const cmsRestore = (pageId, versionId) => http.post(`/cms/pages/${pageId}/restore/${versionId}`).then((r) => r.data);
export const listRestorePoints = () => http.get("/admin/cms/restore-points").then((r) => r.data);
export const createRestorePoint = (label) => http.post("/admin/cms/restore-points", { label }).then((r) => r.data);
export const restoreFullSite = (rid) => http.post(`/admin/cms/restore-points/${rid}/restore`).then((r) => r.data);

// AI Concierge (industry demos)
export const conciergeInfo = (demo) => http.get(`/concierge/${demo}`).then((r) => r.data);
export const conciergeChat = (demo, message, history, sessionId) => http.post("/concierge/chat", { demo, message, history, session_id: sessionId }).then((r) => r.data);
export const conciergeVoice = (formData) => http.post("/concierge/voice", formData).then((r) => r.data);
export const conciergeSpeak = (text, demo) => http.post("/concierge/speak", { text, demo }).then((r) => r.data);
export const conciergeBook = (payload) => http.post("/concierge/book", payload).then((r) => r.data);
export const adminAppointments = (params = {}) => http.get("/admin/appointments", { params }).then((r) => r.data);
export const adminUpdateAppointment = (id, status) => http.patch(`/admin/appointments/${id}`, { status }).then((r) => r.data);
export const shopProducts = (store) => http.get(`/shop/${store}/products`).then((r) => r.data);
export const shopCheckout = (payload) => http.post("/shop/checkout", payload).then((r) => r.data);
export const shopOrderStatus = (sid) => http.get(`/shop/order/${sid}`).then((r) => r.data);
export const shopDonate = (payload) => http.post("/shop/donate", payload).then((r) => r.data);

// AI Receptionist (Phase 1a) — owner CRUD + dashboard
export const receptionistList = (all = false) => http.get("/receptionists", { params: all ? { all: true } : {} }).then((r) => r.data);
export const receptionistCreate = (payload) => http.post("/receptionists", payload).then((r) => r.data);
export const receptionistGet = (id) => http.get(`/receptionists/${id}`).then((r) => r.data);
export const receptionistUpdate = (id, payload) => http.put(`/receptionists/${id}`, payload).then((r) => r.data);
export const receptionistDelete = (id) => http.delete(`/receptionists/${id}`).then((r) => r.data);
export const receptionistCalls = (id) => http.get(`/receptionists/${id}/calls`).then((r) => r.data);
export const receptionistMessages = (id) => http.get(`/receptionists/${id}/messages`).then((r) => r.data);
export const receptionistSummary = (id) => http.get(`/receptionists/${id}/summary`).then((r) => r.data);
// AI Receptionist — public runtime (test console / future embed + telephony)
export const receptionistInfo = (id) => http.get(`/receptionist/${id}/info`).then((r) => r.data);
export const receptionistChat = (id, message, history, session_id) => http.post(`/receptionist/${id}/chat`, { message, history, session_id }).then((r) => r.data);
export const receptionistVoice = (id, formData) => http.post(`/receptionist/${id}/voice`, formData).then((r) => r.data);
export const receptionistSpeak = (id, text, voice) => http.post(`/receptionist/${id}/speak`, { text, voice }).then((r) => r.data);
export const receptionistTakeMessage = (id, payload) => http.post(`/receptionist/${id}/message`, payload).then((r) => r.data);
export const receptionistBook = (id, payload) => http.post(`/receptionist/${id}/book`, payload).then((r) => r.data);
export const receptionistSummarizeCall = (id, cid) => http.post(`/receptionists/${id}/calls/${cid}/summarize`).then((r) => r.data);
export const telephonyStatus = () => http.get("/telephony/status").then((r) => r.data);
export const receptionistDigestTest = (id) => http.post(`/receptionists/${id}/digest/test-send`).then((r) => r.data);
export const receptionistAvailability = (id, date) => http.get(`/receptionist/${id}/availability`, { params: { date } }).then((r) => r.data);
export const receptionistAnalytics = (id, days = 14) => http.get(`/receptionists/${id}/analytics`, { params: { days } }).then((r) => r.data);
export const receptionistAppointments = (id) => http.get(`/receptionists/${id}/appointments`).then((r) => r.data);
export const receptionistRemind = (id, aid) => http.post(`/receptionists/${id}/appointments/${aid}/remind`).then((r) => r.data);

// Communication Center (Phase 1)
export const commsOverview = () => http.get("/comms/overview").then((r) => r.data);
export const commsTemplates = () => http.get("/comms/templates").then((r) => r.data);
export const commsUpsertTemplate = (payload) => http.post("/comms/templates", payload).then((r) => r.data);
export const commsContacts = () => http.get("/comms/contacts").then((r) => r.data);
export const commsUpsertContact = (payload) => http.post("/comms/contacts", payload).then((r) => r.data);
export const commsRecordConsent = (payload) => http.post("/comms/consent", payload).then((r) => r.data);
export const commsSend = (payload) => http.post("/comms/send", payload).then((r) => r.data);
export const commsMessages = (params = {}) => http.get("/comms/messages", { params }).then((r) => r.data);
export const commsSentSettings = () => http.get("/comms/sent-settings").then((r) => r.data);
export const commsSetSentSettings = (payload) => http.put("/comms/sent-settings", payload).then((r) => r.data);
export const commsSimulateInbound = (payload) => http.post("/comms/inbound/simulate", payload).then((r) => r.data);
export const appointmentManage = (aid, t) => http.get(`/appointments/${aid}/manage`, { params: { t } }).then((r) => r.data);
export const appointmentReschedule = (aid, token, date, time) => http.post(`/appointments/${aid}/reschedule`, { token, date, time }).then((r) => r.data);
export const appointmentCancel = (aid, token) => http.post(`/appointments/${aid}/cancel`, { token }).then((r) => r.data);

// ============================================================================
// LCC — Lead & Customer Center
// ============================================================================
export const lccBootstrap = () => http.get("/lcc/bootstrap").then((r) => r.data);
export const lccBackfill = () => http.post("/lcc/backfill").then((r) => r.data);
export const lccCommandCenter = () => http.get("/lcc/command-center").then((r) => r.data);
export const lccContacts = (params = {}) => http.get("/lcc/contacts", { params }).then((r) => r.data);
export const lccContact = (cid) => http.get(`/lcc/contacts/${cid}`).then((r) => r.data);
export const lccCreateContact = (payload) => http.post("/lcc/contacts", payload).then((r) => r.data);
export const lccUpdateContact = (cid, payload) => http.patch(`/lcc/contacts/${cid}`, payload).then((r) => r.data);
export const lccMergeContacts = (payload) => http.post("/lcc/contacts/merge", payload).then((r) => r.data);
export const lccContactDuplicates = (cid) => http.get(`/lcc/contacts/${cid}/duplicates`).then((r) => r.data);
export const lccLeads = (params = {}) => http.get("/lcc/leads", { params }).then((r) => r.data);
export const lccCreateLead = (payload) => http.post("/lcc/leads", payload).then((r) => r.data);
export const lccUpdateLead = (lid, payload) => http.patch(`/lcc/leads/${lid}`, payload).then((r) => r.data);
export const lccRescoreLead = (lid) => http.post(`/lcc/leads/${lid}/rescore`).then((r) => r.data);
export const lccPipelines = () => http.get("/lcc/pipelines").then((r) => r.data);
export const lccOpportunities = (params = {}) => http.get("/lcc/opportunities", { params }).then((r) => r.data);
export const lccCreateOpp = (payload) => http.post("/lcc/opportunities", payload).then((r) => r.data);
export const lccUpdateOpp = (oid, payload) => http.patch(`/lcc/opportunities/${oid}`, payload).then((r) => r.data);
export const lccTasks = (params = {}) => http.get("/lcc/tasks", { params }).then((r) => r.data);
export const lccCreateTask = (payload) => http.post("/lcc/tasks", payload).then((r) => r.data);
export const lccUpdateTask = (tid, payload) => http.patch(`/lcc/tasks/${tid}`, payload).then((r) => r.data);
export const lccActivities = (params = {}) => http.get("/lcc/activities", { params }).then((r) => r.data);
export const lccApprovals = (params = {}) => http.get("/lcc/approvals", { params }).then((r) => r.data);
export const lccDecideApproval = (aid, payload) => http.patch(`/lcc/approvals/${aid}`, payload).then((r) => r.data);
export const lccAudit = (params = {}) => http.get("/lcc/audit", { params }).then((r) => r.data);

// LCC — Companies / Routing / Inbox / Enhanced Approvals
export const lccCompanies = (params = {}) => http.get("/lcc/companies", { params }).then((r) => r.data);
export const lccCompany = (id) => http.get(`/lcc/companies/${id}`).then((r) => r.data);
export const lccCreateCompany = (payload) => http.post("/lcc/companies", payload).then((r) => r.data);
export const lccUpdateCompany = (id, payload) => http.patch(`/lcc/companies/${id}`, payload).then((r) => r.data);
export const lccRoutingRules = () => http.get("/lcc/routing/rules").then((r) => r.data);
export const lccCreateRoutingRule = (p) => http.post("/lcc/routing/rules", p).then((r) => r.data);
export const lccUpdateRoutingRule = (id, p) => http.patch(`/lcc/routing/rules/${id}`, p).then((r) => r.data);
export const lccDeleteRoutingRule = (id) => http.delete(`/lcc/routing/rules/${id}`).then((r) => r.data);
export const lccTestRouting = (p) => http.post("/lcc/routing/test", p).then((r) => r.data);
export const lccInboxThreads = (params = {}) => http.get("/lcc/inbox/threads", { params }).then((r) => r.data);
export const lccInboxThread = (cid) => http.get(`/lcc/inbox/threads/${cid}`).then((r) => r.data);
export const lccInboxReply = (cid, payload) => http.post(`/lcc/inbox/threads/${cid}/reply`, payload).then((r) => r.data);

// WTC — WebsiteTech Center (Core Foundation)
export const wtcBootstrap = () => http.get("/wtc/bootstrap").then((r) => r.data);
export const wtcBackfill = () => http.post("/wtc/backfill").then((r) => r.data);
export const wtcProfile = () => http.get("/wtc/profile").then((r) => r.data);
export const wtcUpdateProfile = (p) => http.patch("/wtc/profile", p).then((r) => r.data);
export const wtcKnowledge = (params = {}) => http.get("/wtc/knowledge", { params }).then((r) => r.data);
export const wtcKnowledgeItem = (kid) => http.get(`/wtc/knowledge/${kid}`).then((r) => r.data);
export const wtcCreateKnowledge = (p) => http.post("/wtc/knowledge", p).then((r) => r.data);
export const wtcUpdateKnowledge = (kid, p) => http.patch(`/wtc/knowledge/${kid}`, p).then((r) => r.data);
export const wtcApproveKnowledge = (kid) => http.post(`/wtc/knowledge/${kid}/approve`).then((r) => r.data);
export const wtcArchiveKnowledge = (kid) => http.post(`/wtc/knowledge/${kid}/archive`).then((r) => r.data);
export const wtcGaps = (params = {}) => http.get("/wtc/gaps", { params }).then((r) => r.data);
export const wtcAnswerGap = (gid, p) => http.post(`/wtc/gaps/${gid}/answer`, p).then((r) => r.data);
export const wtcDismissGap = (gid) => http.post(`/wtc/gaps/${gid}/dismiss`).then((r) => r.data);
export const wtcRecordGap = (p) => http.post("/wtc/gaps", p).then((r) => r.data);
export const wtcIntegrations = () => http.get("/wtc/integrations").then((r) => r.data);
export const wtcAddIntegration = (p) => http.post("/wtc/integrations", p).then((r) => r.data);
export const wtcRemoveIntegration = (kind) => http.delete(`/wtc/integrations/${kind}`).then((r) => r.data);
export const wtcTestIntegration = (kind) => http.post(`/wtc/integrations/${kind}/test`).then((r) => r.data);
export const wtcUsage = (params = {}) => http.get("/wtc/usage", { params }).then((r) => r.data);
export const wtcEntitlements = () => http.get("/wtc/entitlements").then((r) => r.data);
export const wtcSetEntitlement = (p) => http.post("/wtc/entitlements", p).then((r) => r.data);
export const wtcActionCenter = () => http.get("/wtc/action-center").then((r) => r.data);

// WTC — Phase 1.4: Audit / Trust / Onboarding / Suggest
export const wtcRunAudit = (payload) => http.post("/wtc/audit/run", payload).then((r) => r.data);
export const wtcAuditFindings = (params = {}) => http.get("/wtc/audit/findings", { params }).then((r) => r.data);
export const wtcUpdateAuditFinding = (fid, payload) => http.patch(`/wtc/audit/findings/${fid}`, payload).then((r) => r.data);
export const wtcTrustProfile = () => http.get("/wtc/trust").then((r) => r.data);
export const wtcUpdateTrustProfile = (p) => http.patch("/wtc/trust", p).then((r) => r.data);
export const wtcRunTrustChecks = (payload) => http.post("/wtc/trust/checks/run", payload).then((r) => r.data);
export const wtcTrustHistory = (params) => http.get("/wtc/trust/history", { params }).then((r) => r.data);
export const wtcSuggestKnowledge = (q, k = 3) => http.get("/wtc/knowledge/suggest", { params: { q, k } }).then((r) => r.data);


// ============================================================================
// AI Digital Representative (WebsiteTech AI Representative)
// ============================================================================
export const repBootstrap = () => http.get("/rep/bootstrap").then((r) => r.data);
export const repList = () => http.get("/rep").then((r) => r.data);
export const repCreate = (p) => http.post("/rep", p).then((r) => r.data);
export const repGet = (id) => http.get(`/rep/${id}`).then((r) => r.data);
export const repUpdate = (id, p) => http.patch(`/rep/${id}`, p).then((r) => r.data);
export const repPublish = (id) => http.post(`/rep/${id}/publish`).then((r) => r.data);
export const repArchive = (id) => http.post(`/rep/${id}/archive`).then((r) => r.data);
export const repVersions = (id) => http.get(`/rep/${id}/versions`).then((r) => r.data);
export const repRollback = (id, v) => http.post(`/rep/${id}/rollback/${v}`).then((r) => r.data);
export const repAudit = (id) => http.get(`/rep/${id}/audit`).then((r) => r.data);
export const repConsents = (id) => http.get(`/rep/${id}/consents`).then((r) => r.data);
export const repConsentAdd = (id, p) => http.post(`/rep/${id}/consents`, p).then((r) => r.data);
export const repConsentWithdraw = (id, cid) => http.post(`/rep/${id}/consents/${cid}/withdraw`).then((r) => r.data);
export const repKnowledgeAvailable = (id) => http.get(`/rep/${id}/knowledge/available`).then((r) => r.data);
export const repTestChat = (id, p) => http.post(`/rep/${id}/test-chat`, p).then((r) => r.data);
export const repRunAction = (id, p) => http.post(`/rep/${id}/actions/run`, p).then((r) => r.data);
export const repExecutions = (id, status) => http.get(`/rep/${id}/actions/executions`, { params: status ? { status } : {} }).then((r) => r.data);
export const repConversations = (id) => http.get(`/rep/${id}/conversations`).then((r) => r.data);
export const repConversationMessages = (id, cid) => http.get(`/rep/${id}/conversations/${cid}/messages`).then((r) => r.data);
export const repIntelligence = (id) => http.get(`/rep/${id}/intelligence`).then((r) => r.data);
export const repTemplates = () => http.get("/rep/templates").then((r) => r.data);
export const repTemplate = (key) => http.get(`/rep/templates/${key}`).then((r) => r.data);
export const repExport = (id) => http.get(`/rep/${id}/export`).then((r) => r.data);
export const repDeleteConversations = (id) => http.post(`/rep/${id}/delete-conversations`).then((r) => r.data);

// ============================================================================
// AI Feedback Assistant (Customer Voice AI)
// ============================================================================
export const fbBootstrap = () => http.get("/fb/bootstrap").then((r) => r.data);
export const fbTemplates = () => http.get("/fb/templates").then((r) => r.data);
export const fbTemplate = (key) => http.get(`/fb/templates/${key}`).then((r) => r.data);
export const fbDraft = (request, template_key, languages) => http.post("/fb/draft", { request, template_key, languages }).then((r) => r.data);
export const fbSurveys = () => http.get("/fb/surveys").then((r) => r.data);
export const fbCreateSurvey = (draft) => http.post("/fb/surveys", { draft }).then((r) => r.data);
export const fbSurvey = (sid) => http.get(`/fb/surveys/${sid}`).then((r) => r.data);
export const fbUpdateSurvey = (sid, p) => http.patch(`/fb/surveys/${sid}`, p).then((r) => r.data);
export const fbActivateSurvey = (sid) => http.post(`/fb/surveys/${sid}/activate`).then((r) => r.data);
export const fbSurveyLink = (sid) => http.get(`/fb/surveys/${sid}/link`).then((r) => r.data);
export const fbCampaigns = (sid) => http.get(`/fb/surveys/${sid}/campaigns`).then((r) => r.data);
export const fbCreateCampaign = (sid, p) => http.post(`/fb/surveys/${sid}/campaigns`, p).then((r) => r.data);
export const fbSendCampaign = (cid) => http.post(`/fb/campaigns/${cid}/send`).then((r) => r.data);
export const fbResponses = (sid) => http.get(`/fb/surveys/${sid}/responses`).then((r) => r.data);
export const fbInsights = (sid) => http.get(`/fb/surveys/${sid}/insights`).then((r) => r.data);
export const fbAggregate = (sid, min = 20) => http.get(`/fb/surveys/${sid}/aggregate`, { params: { min_responses: min } }).then((r) => r.data);
export const fbAlerts = (status) => http.get("/fb/alerts", { params: status ? { status } : {} }).then((r) => r.data);
export const fbUpdateAlert = (aid, p) => http.patch(`/fb/alerts/${aid}`, p).then((r) => r.data);
export const fbTestimonials = (status) => http.get("/fb/testimonials", { params: status ? { status } : {} }).then((r) => r.data);
export const fbApproveTestimonial = (tid, p) => http.post(`/fb/testimonials/${tid}/approve`, p).then((r) => r.data);
export const fbRejectTestimonial = (tid) => http.post(`/fb/testimonials/${tid}/reject`).then((r) => r.data);
export const fbPublishQueue = () => http.get("/fb/publish-queue").then((r) => r.data);
export const fbApplyPublish = (pid) => http.post(`/fb/publish-queue/${pid}/apply`).then((r) => r.data);
export const fbDashboard = () => http.get("/fb/dashboard").then((r) => r.data);


// ============================================================================
// AI Voice & Communication Platform
// ============================================================================
export const vpMissedSettings = () => http.get("/vp/missed-calls/settings").then((r) => r.data);
export const vpMissedSettingsPatch = (p) => http.patch("/vp/missed-calls/settings", p).then((r) => r.data);
export const vpMissedList = (status) => http.get("/vp/missed-calls", { params: status ? { status } : {} }).then((r) => r.data);
export const vpMissedRecover = (mid) => http.post(`/vp/missed-calls/${mid}/mark-recovered`).then((r) => r.data);
export const vpPaymentGet = (iid) => http.get(`/vp/payments/${iid}`).then((r) => r.data);
export const vpPaymentFulfill = (iid, p = {}) => http.post(`/vp/payments/${iid}/fulfill`, p).then((r) => r.data);
export const vpCopilotSuggest = (p) => http.post("/vp/copilot/suggest", p).then((r) => r.data);
export const vpCopilotEvents = (limit = 50) => http.get("/vp/copilot/events", { params: { limit } }).then((r) => r.data);


// ============================================================================
// Public "Talk to Our AI Employee" demo
// ============================================================================
export const publicDemoInfo = () => http.get("/public-demo/info").then((r) => r.data);
export const publicDemoStart = (p = {}) => http.post("/public-demo/session/start", p).then((r) => r.data);
export const publicDemoChat = (repId, session_id, message, language) =>
  http.post(`/rep/public/${repId}/chat`, {
    session_id, message,
    visitor_meta: language ? { preferred_language: language } : undefined,
  }).then((r) => r.data);
export const publicDemoVoice = (repId, session_id, audio_b64, mime = "audio/webm", language) =>
  http.post(`/rep/public/${repId}/voice`, {
    session_id, audio_b64, mime, play_reply: true,
    visitor_meta: language ? { preferred_language: language } : undefined,
  }).then((r) => r.data);
export const publicDemoLead = (payload) => http.post("/public-demo/lead", payload).then((r) => r.data);
export const publicDemoEnd = (session_id) =>
  http.post(`/public-demo/session/end?session_id=${encodeURIComponent(session_id)}`).then((r) => r.data);


// ============================================================================
// AI-generated Feature Infographics
// ============================================================================
export const infographicInfo = (slug) => http.get(`/infographics/${slug}/info`).then((r) => r.data);
export const infographicCatalog = () => http.get("/infographics").then((r) => r.data);
export const infographicImageUrl = (slug) => `${API}/infographics/${slug}.png`;
export const infographicSectionImageUrl = (slug, section) => `${API}/infographics/${slug}/${section}.png`;
export const infographicSectionInfo = (slug, section) => http.get(`/infographics/${slug}/${section}/info`).then((r) => r.data);
export const infographicSectionsList = () => http.get(`/infographics/sections/list`).then((r) => r.data);


// ============================================================================
// Trust & Compliance Phase 2 (questionnaires, ai systems, vendors, access reviews)
// ============================================================================
export const tpDashboard = () => http.get("/tp/dashboard").then((r) => r.data);
export const tpReminders = (horizon = 30) => http.get(`/tp/reminders?horizon_days=${horizon}`).then((r) => r.data);
// questionnaires
export const tpQuestionnaires = () => http.get("/tp/questionnaires").then((r) => r.data);
export const tpQuestionnaireCreate = (p) => http.post("/tp/questionnaires", p).then((r) => r.data);
export const tpQuestionnaireGet = (id) => http.get(`/tp/questionnaires/${id}`).then((r) => r.data);
export const tpQuestionnairePatch = (id, p) => http.patch(`/tp/questionnaires/${id}`, p).then((r) => r.data);
export const tpQuestionnaireAnswer = (id, p) => http.post(`/tp/questionnaires/${id}/answers`, p).then((r) => r.data);
// ai systems
export const tpAiSystems = () => http.get("/tp/ai-systems").then((r) => r.data);
export const tpAiSystemCreate = (p) => http.post("/tp/ai-systems", p).then((r) => r.data);
export const tpAiSystemPatch = (id, p) => http.patch(`/tp/ai-systems/${id}`, p).then((r) => r.data);
export const tpAiSystemDelete = (id) => http.delete(`/tp/ai-systems/${id}`).then((r) => r.data);
// vendors
export const tpVendors = () => http.get("/tp/vendors").then((r) => r.data);
export const tpVendorCreate = (p) => http.post("/tp/vendors", p).then((r) => r.data);
export const tpVendorPatch = (id, p) => http.patch(`/tp/vendors/${id}`, p).then((r) => r.data);
export const tpVendorDelete = (id) => http.delete(`/tp/vendors/${id}`).then((r) => r.data);
// access reviews
export const tpAccessReviews = () => http.get("/tp/access-reviews").then((r) => r.data);
export const tpAccessReviewCreate = (p) => http.post("/tp/access-reviews", p).then((r) => r.data);
export const tpAccessReviewDecide = (rid, iid, p) => http.patch(`/tp/access-reviews/${rid}/items/${iid}`, p).then((r) => r.data);
export const tpAccessReviewComplete = (rid) => http.post(`/tp/access-reviews/${rid}/complete`).then((r) => r.data);
// evidence
export const tpEvidenceUpload = (p) => http.post("/tp/evidence", p).then((r) => r.data);
export const tpEvidenceList = (entity_kind, entity_id) =>
  http.get("/tp/evidence", { params: { entity_kind, entity_id } }).then((r) => r.data);


// ============================================================================
// Workflow Automation Builder
// ============================================================================
export const wfCatalog = () => http.get("/wf/catalog").then((r) => r.data);
export const wfWorkflows = () => http.get("/wf/workflows").then((r) => r.data);
export const wfCreate = (p) => http.post("/wf/workflows", p).then((r) => r.data);
export const wfGet = (id) => http.get(`/wf/workflows/${id}`).then((r) => r.data);
export const wfPatch = (id, p) => http.patch(`/wf/workflows/${id}`, p).then((r) => r.data);
export const wfDelete = (id) => http.delete(`/wf/workflows/${id}`).then((r) => r.data);
export const wfTestRun = (id, payload = {}) => http.post(`/wf/workflows/${id}/test-run`, { payload }).then((r) => r.data);


// ============================================================================
// AI Business Center (Sprint 1)
// ============================================================================
export const bcMethodology = () => http.get("/bc/methodology").then((r) => r.data);
export const bcAssessmentQuestions = () => http.get("/bc/assessment/questions").then((r) => r.data);
export const bcAssessmentStart = (p = {}) => http.post("/bc/assessment/start", p).then((r) => r.data);
export const bcAssessmentAnswer = (p) => http.post("/bc/assessment/answer", p).then((r) => r.data);
export const bcAssessmentComplete = (p) => http.post("/bc/assessment/complete", p).then((r) => r.data);
export const bcAssessmentGet = (aid) => http.get(`/bc/assessment/${aid}`).then((r) => r.data);
export const bcTools = (category) => http.get("/bc/tools", { params: category ? { category } : {} }).then((r) => r.data);
export const bcToolGet = (slug) => http.get(`/bc/tools/${slug}`).then((r) => r.data);
export const bcCategories = () => http.get("/bc/categories").then((r) => r.data);
// Sprint 2
export const bcStackGenerate = (p) => http.post("/bc/stack/generate", p).then((r) => r.data);
export const bcStackGet = (id) => http.get(`/bc/stack/${id}`).then((r) => r.data);
export const bcPlaybooks = () => http.get("/bc/playbooks").then((r) => r.data);
export const bcPlaybookGet = (slug) => http.get(`/bc/playbooks/${slug}`).then((r) => r.data);
export const bcCalculators = () => http.get("/bc/calculators").then((r) => r.data);
export const bcCalculatorGet = (kind) => http.get(`/bc/calculators/${kind}`).then((r) => r.data);
export const bcCalculatorCompute = (kind, p) => http.post(`/bc/calculators/${kind}/compute`, p).then((r) => r.data);
export const bcReviews = (state) => http.get("/bc/reviews", { params: state ? { state } : {} }).then((r) => r.data);
export const bcReviewCreate = (p) => http.post("/bc/reviews", p).then((r) => r.data);
export const bcReviewGet = (id) => http.get(`/bc/reviews/${id}`).then((r) => r.data);
export const bcReviewPatch = (id, p) => http.patch(`/bc/reviews/${id}`, p).then((r) => r.data);
export const bcReviewTransition = (id, to_state, note = "") => http.post(`/bc/reviews/${id}/transition`, { to_state, note }).then((r) => r.data);
export const bcReviewStates = () => http.get("/bc/reviews/states").then((r) => r.data);

// Sprint 3 — Compare
export const bcCompareDimensions = () => http.get("/bc/compare/dimensions").then((r) => r.data);
export const bcCompare = (p) => http.post("/bc/compare", p).then((r) => r.data);
export const bcComparisonGet = (id) => http.get(`/bc/comparisons/${id}`).then((r) => r.data);

// Sprint 3 — Guides
export const bcGuides = (params = {}) => http.get("/bc/guides", { params }).then((r) => r.data);
export const bcGuideGet = (slug) => http.get(`/bc/guides/${slug}`).then((r) => r.data);

// Sprint 3 — Newsletter
export const bcBriefSegments = () => http.get("/bc/brief/segments").then((r) => r.data);
export const bcBriefSubscribe = (p) => http.post("/bc/brief/subscribe", p).then((r) => r.data);
export const bcBriefConfirm = (token) => http.post("/bc/brief/confirm", { token }).then((r) => r.data);
export const bcBriefGetPrefs = (unsub_token) => http.get(`/bc/brief/preferences/${unsub_token}`).then((r) => r.data);
export const bcBriefUpdatePrefs = (p) => http.post("/bc/brief/preferences", p).then((r) => r.data);
export const bcBriefStats = () => http.get("/bc/brief/stats").then((r) => r.data);
export const bcBriefEditions = (segment) => http.get("/bc/brief/editions", { params: segment ? { segment } : {} }).then((r) => r.data);
export const bcBriefEditionCreate = (p) => http.post("/bc/brief/editions", p).then((r) => r.data);
export const bcBriefEditionSend = (eid) => http.post(`/bc/brief/editions/${eid}/send`).then((r) => r.data);
export const bcBriefSegmentPatch = (key, p) => http.patch(`/bc/brief/segments/${key}`, p).then((r) => r.data);

// AI Lead Response & Calling — Sprint A
export const lrProductInfo = () => http.get("/lr/product-info").then((r) => r.data);
export const lrSettings = () => http.get("/lr/settings").then((r) => r.data);
export const lrSettingsPatch = (p) => http.patch("/lr/settings", p).then((r) => r.data);
export const lrInbox = (status) => http.get("/lr/inbox", { params: status ? { status } : {} }).then((r) => r.data);
export const lrStats = () => http.get("/lr/stats").then((r) => r.data);
export const lrLeadTimeline = (lid) => http.get(`/lr/leads/${lid}/timeline`).then((r) => r.data);

// Number Health Center
export const nhOverview = () => http.get("/nh/overview").then((r) => r.data);
export const nhNumbers = () => http.get("/nh/numbers").then((r) => r.data);
export const nhNumberGet = (nid) => http.get(`/nh/numbers/${nid}`).then((r) => r.data);
export const nhNumberAdd = (p) => http.post("/nh/numbers", p).then((r) => r.data);
export const nhNumberPatch = (nid, p) => http.patch(`/nh/numbers/${nid}`, p).then((r) => r.data);
export const nhNumberPause = (nid, reason) => http.post(`/nh/numbers/${nid}/pause`, { reason }).then((r) => r.data);
export const nhNumberRestore = (nid, note = "") => http.post(`/nh/numbers/${nid}/restore`, { note }).then((r) => r.data);
export const nhEventRecord = (p) => http.post("/nh/events", p).then((r) => r.data);
export const nhPolicyGet = () => http.get("/nh/policy").then((r) => r.data);
export const nhPolicyPatch = (p) => http.patch("/nh/policy", p).then((r) => r.data);

// Sprint B — Dialer Workspace
export const dialerPresence = (status, note) => http.post("/dialer/presence", { status, note }).then((r) => r.data);
export const dialerRoster = () => http.get("/dialer/team/roster").then((r) => r.data);
export const dialerQueue = () => http.get("/dialer/queue").then((r) => r.data);
export const dialerClaim = (lid) => http.post(`/dialer/leads/${lid}/claim`).then((r) => r.data);
export const dialerRelease = (lid, reason) => http.post(`/dialer/leads/${lid}/release`, { reason }).then((r) => r.data);
export const dialerClickToCall = (p) => http.post("/dialer/click-to-call", p).then((r) => r.data);
export const dialerDisposition = (lid, p) => http.post(`/dialer/leads/${lid}/disposition`, p).then((r) => r.data);
export const dialerHistory = (lid) => http.get(`/dialer/leads/${lid}/history`).then((r) => r.data);
export const dialerStats = (days = 7) => http.get("/dialer/stats", { params: { days } }).then((r) => r.data);

// Multilingual templates + missed-call callback
export const lrTemplates = () => http.get("/lr/templates").then((r) => r.data);
export const lrTemplateUpsert = (p) => http.post("/lr/templates", p).then((r) => r.data);
export const lrTemplateRender = (p) => http.post("/lr/templates/render", p).then((r) => r.data);
export const lrMissedCallbacks = (status) => http.get("/lr/missed-call/callbacks", { params: status ? { status } : {} }).then((r) => r.data);
export const lrMissedCallbackAdvance = (cid, outcome) => http.post(`/lr/missed-call/callbacks/${cid}/advance`, { outcome }).then((r) => r.data);


// AI Assistants Hub
export const assistantsCatalog = () => http.get("/assistants/catalog").then((r) => r.data);
export const assistantWebsiteAudit = (p) => http.post("/assistants/website-audit", p).then((r) => r.data);
export const assistantProposalWriter = (p) => http.post("/assistants/proposal-writer", p).then((r) => r.data);
export const assistantSalesCoach = (p) => http.post("/assistants/sales-coach", p).then((r) => r.data);
export const assistantsAnalytics = (days = 30) => http.get("/assistants/analytics", { params: { days } }).then((r) => r.data);
export const assistantProposalStreamUrl = () => `${API}/assistants/proposal-writer/stream`;

// LitLab AI Agent (Iris) — separate tenant, LitLab-scoped KB
export const litlabAgentInfo = () => http.get("/litlab/agent/info").then((r) => r.data);
export const litlabAgentStart = (p = {}) => http.post("/litlab/agent/session/start", p).then((r) => r.data);
// FAQ-cached ask: instant when known, LLM fallback for new questions
export const litlabAgentAsk = (question, session_id, visitor_meta) =>
  http.post("/litlab/agent/ask",
    { question, session_id, visitor_meta }).then((r) => r.data);
// CMS — services popup articles
export const litlabArticles = (kind) =>
  http.get("/litlab/articles", { params: kind ? { kind } : {} })
    .then((r) => r.data);
export const litlabArticlePut = (kind, slug, payload) =>
  http.put(`/litlab/articles/${kind}/${slug}`, payload).then((r) => r.data);
// Iris QnA cache admin
export const litlabQnaList = (limit = 200) =>
  http.get("/litlab/agent/qna", { params: { limit } }).then((r) => r.data);
export const litlabQnaPut = (qid, payload) =>
  http.put(`/litlab/agent/qna/${encodeURIComponent(qid)}`, payload)
    .then((r) => r.data);
export const litlabQnaDelete = (qid) =>
  http.delete(`/litlab/agent/qna/${encodeURIComponent(qid)}`)
    .then((r) => r.data);
export const litlabAgentLead = (p) => http.post("/litlab/agent/lead", p).then((r) => r.data);
export const litlabAgentEnd = (session_id) => http.post(`/litlab/agent/session/end?session_id=${encodeURIComponent(session_id)}`).then((r) => r.data);
export const litlabAgentQuoteSubmit = (p) => http.post("/litlab/agent/quote-submit", p).then((r) => r.data);
export const assistantsAlertsConfigGet = () => http.get("/assistants/alerts/config").then((r) => r.data);
export const assistantsAlertsConfigPut = (p) => http.put("/assistants/alerts/config", p).then((r) => r.data);
export const assistantsAlertsHistory = (limit = 20) => http.get("/assistants/alerts/history", { params: { limit } }).then((r) => r.data);
export const assistantsAlertsTest = () => http.post("/assistants/alerts/test").then((r) => r.data);
export const litlabAgentQuoteGet = (quote_id) => http.get(`/litlab/agent/quote/${quote_id}`).then((r) => r.data);

// Iris analytics + SLA
export const litlabAgentAnalytics = (days = 30) => http.get("/litlab/agent/analytics", { params: { days } }).then((r) => r.data);
export const litlabAgentSlaCheck = () => http.post("/litlab/agent/sla-check").then((r) => r.data);
export const litlabAgentCsat = (p) => http.post("/litlab/agent/csat", p).then((r) => r.data);
export const litlabAgentCsatVoicePrompt = (language = "en") => http.post(`/litlab/agent/csat/voice-prompt?language=${language}`).then((r) => r.data);
export const litlabAgentCsatVoice = (p) => http.post("/litlab/agent/csat/voice", p).then((r) => r.data);
export const litlabAgentVoiceTour = (language = "en") => http.post(`/litlab/agent/voice-tour?language=${language}`).then((r) => r.data);
export const litlabAgentWeeklyDigestPreview = () => http.get("/litlab/agent/weekly-digest/preview").then((r) => r.data);
export const litlabAgentWeeklyDigestSend = (force = false) => http.post(`/litlab/agent/weekly-digest/send?force=${force}`).then((r) => r.data);

// ---- AI Commerce Intelligence ----
export const commerceSite = () => http.get("/commerce/site").then((r) => r.data);
export const commerceDemoSummary = () => http.get("/commerce/demo/summary").then((r) => r.data);
export const commerceDemoRecs = () => http.get("/commerce/demo/recommendations").then((r) => r.data);

export const commerceSettings = () => http.get("/commerce/settings").then((r) => r.data);
export const commerceSettingsUpdate = (p) => http.put("/commerce/settings", p).then((r) => r.data);
export const commerceSummary = (days = 30) => http.get("/commerce/summary", { params: { days } }).then((r) => r.data);

export const commerceProducts = (params = {}) => http.get("/commerce/products", { params }).then((r) => r.data);
export const commerceProductUpsert = (p) => http.post("/commerce/products", p).then((r) => r.data);
export const commerceProductsBulk = (products) => http.post("/commerce/products/bulk", { products }).then((r) => r.data);
export const commerceProductsImportCsv = (csv_text) => http.post("/commerce/products/import-csv", { csv_text }).then((r) => r.data);
export const commerceProductsImportFile = (kind, data_base64, filename) => http.post("/commerce/products/import-file", { kind, data_base64, filename }).then((r) => r.data);
export const commerceOrderAdd = (o) => http.post("/commerce/orders", o).then((r) => r.data);

export const commerceCompetitors = () => http.get("/commerce/competitors").then((r) => r.data);
export const commerceCompetitorAdd = (c) => http.post("/commerce/competitors", c).then((r) => r.data);
export const commerceCompetitorVerify = (id) => http.post(`/commerce/competitors/${id}/verify`).then((r) => r.data);
export const commerceCompetitorSnapshot = (s) => http.post("/commerce/competitors/snapshot", s).then((r) => r.data);
export const commerceCompetitorHistory = (id) => http.get(`/commerce/competitors/${id}/history`).then((r) => r.data);

export const commerceGuardrails = () => http.get("/commerce/guardrails").then((r) => r.data);
export const commerceGuardrailsUpdate = (g) => http.put("/commerce/guardrails", g).then((r) => r.data);

export const commerceRecsList = (params = {}) => http.get("/commerce/recommendations", { params }).then((r) => r.data);
export const commerceRecsGenerate = () => http.post("/commerce/recommendations/generate").then((r) => r.data);
export const commerceRecsDecision = (id, p) => http.post(`/commerce/recommendations/${id}/decision`, p).then((r) => r.data);
export const commerceRecsExecute = (id) => http.post(`/commerce/recommendations/${id}/execute`).then((r) => r.data);
export const commerceRecsRollback = (id) => http.post(`/commerce/recommendations/${id}/rollback`).then((r) => r.data);
export const commerceActions = (limit = 100) => http.get("/commerce/actions", { params: { limit } }).then((r) => r.data);

export const commerceWeeklyPreview = () => http.get("/commerce/weekly-report/preview").then((r) => r.data);

export const commerceIntegrations = () => http.get("/commerce/integrations").then((r) => r.data);
export const commerceConnectShopify = (p) => http.post("/commerce/connect/shopify", p).then((r) => r.data);
export const commerceConnectWoo = (p) => http.post("/commerce/connect/woocommerce", p).then((r) => r.data);

export const commerceDemoSeed = (force = false) => http.post(`/commerce/demo/seed?force=${force}`).then((r) => r.data);

// SSE stream for /commerce/ask/stream — caller uses fetch+ReadableStream
export const commerceAskStreamUrl = () => `${API}/commerce/ask/stream`;

// ---- AI Commerce: Shopify sync + Weekly recipients + Promotions ----
export const commerceShopifySyncNow = () => http.post("/commerce/integrations/shopify/sync").then((r) => r.data);
export const commerceWeeklyRecipients = () => http.get("/commerce/weekly-report/recipients").then((r) => r.data);
export const commerceWeeklyRecipientsSet = (recipients) => http.put("/commerce/weekly-report/recipients", { recipients }).then((r) => r.data);
export const commerceWeeklySendNow = (force = false) => http.post(`/commerce/weekly-report/send?force=${force}`).then((r) => r.data);
export const commercePromoSimulate = (p) => http.post("/commerce/promotions/simulate", p).then((r) => r.data);
export const commercePromoLaunch = (p) => http.post("/commerce/promotions", p).then((r) => r.data);
export const commercePromoList = (status) => http.get("/commerce/promotions", { params: status ? { status } : {} }).then((r) => r.data);
export const commercePromoClose = (id) => http.post(`/commerce/promotions/${id}/close`).then((r) => r.data);
// ---- Commerce Phase A+B additions ----
export const commerceAuditList = (params = {}) => http.get("/commerce/audit", { params }).then((r) => r.data);
export const commerceTeamList = () => http.get("/commerce/team").then((r) => r.data);
export const commerceTeamUpsert = (m) => http.post("/commerce/team", m).then((r) => r.data);
export const commerceTeamRemove = (email) => http.delete(`/commerce/team/${encodeURIComponent(email)}`).then((r) => r.data);
export const commerceInventoryHealth = (days = 30) => http.get("/commerce/inventory/health", { params: { days } }).then((r) => r.data);
export const commerceCompetitorsChanges = (hours = 168) => http.get("/commerce/competitors/changes", { params: { hours } }).then((r) => r.data);
export const commerceBacktest = (days = 90) => http.get("/commerce/backtest", { params: { days } }).then((r) => r.data);
export const commerceRecsAssign = (id, p) => http.post(`/commerce/recommendations/${id}/assign`, p).then((r) => r.data);
export const commerceConnectWooCreds = (p) => http.post("/commerce/connect/woocommerce/credentials", p).then((r) => r.data);
export const commerceWooSyncNow = () => http.post("/commerce/integrations/woocommerce/sync").then((r) => r.data);
export const commerceReuseAdapters = () => http.get("/commerce/reuse/adapters").then((r) => r.data);
// Phase D — forecasting + bundles + Slack Block-Kit
export const commerceForecastSummary = (params = {}) => http.get("/commerce/forecast", { params }).then((r) => r.data);
export const commerceForecastSku = (sku, params = {}) => http.get(`/commerce/forecast/${encodeURIComponent(sku)}`, { params }).then((r) => r.data);
export const commerceBundles = (params = {}) => http.get("/commerce/bundles/recommendations", { params }).then((r) => r.data);
export const commerceSlackWebhookGet = () => http.get("/commerce/weekly-report/slack-webhook").then((r) => r.data);
export const commerceSlackWebhookPut = (slack_webhook_url) => http.put("/commerce/weekly-report/slack-webhook", { slack_webhook_url }).then((r) => r.data);
export const commerceSlackPreview = () => http.post("/commerce/weekly-report/slack-preview").then((r) => r.data);
export const commerceSlackSend = () => http.post("/commerce/weekly-report/slack-send").then((r) => r.data);
// Phase D+ — location competitor analysis + ad-cost aware margins
export const commerceCompetitorRegions = (hours = 720) => http.get("/commerce/competitors/regions", { params: { hours } }).then((r) => r.data);
export const commerceAdsSummary = (days = 30) => http.get("/commerce/ads/summary", { params: { days } }).then((r) => r.data);
export const commerceAdsList = (params = {}) => http.get("/commerce/ads/spend", { params }).then((r) => r.data);
export const commerceAdsImport = (rows) => http.post("/commerce/ads/spend", { rows }).then((r) => r.data);
export const commerceAdsImportCsv = (csv_text, default_source = "google_ads") => http.post("/commerce/ads/spend/import-csv", { csv_text, default_source }).then((r) => r.data);
// Phase D++ — Image match, WhatsApp, Autonomous publish, Ads OAuth
export const commerceImageMatch = (image_base64, hint) => http.post("/commerce/competitors/image-match", { image_base64, hint }).then((r) => r.data);
export const commerceWhatsAppSend = (rec_id, to_phone) => http.post("/commerce/whatsapp/approval-request", { rec_id, to_phone }).then((r) => r.data);
export const commerceWhatsAppOutbox = () => http.get("/commerce/whatsapp/outbox").then((r) => r.data);
export const commerceAutonomousGet = () => http.get("/commerce/autonomous/settings").then((r) => r.data);
export const commerceAutonomousSet = (enabled) => http.put("/commerce/autonomous/settings", { enabled }).then((r) => r.data);
export const commerceRecPublish = (id) => http.post(`/commerce/recommendations/${id}/publish`).then((r) => r.data);
export const commerceAdsIntegrations = () => http.get("/commerce/ads/integrations").then((r) => r.data);
export const commerceAdsIntegrationConnect = (p) => http.post("/commerce/ads/integrations/connect", p).then((r) => r.data);
export const commerceAdsIntegrationSyncNow = (source) => http.post(`/commerce/ads/integrations/${source}/sync-now`).then((r) => r.data);
// Phase D+++ — Prompt library, OAuth authorize, publish rollback
export const commercePromptsList = (category) => http.get("/commerce/prompts", { params: category ? { category } : {} }).then((r) => r.data);
export const commercePromptsAdd = (p) => http.post("/commerce/prompts", p).then((r) => r.data);
export const commercePromptsUpdate = (id, p) => http.put(`/commerce/prompts/${id}`, p).then((r) => r.data);
export const commercePromptsDelete = (id) => http.delete(`/commerce/prompts/${id}`).then((r) => r.data);
export const commerceAdsOAuthAuthorize = (source) => http.get(`/commerce/ads/integrations/${source}/oauth/authorize`).then((r) => r.data);
export const commerceRecPublishRollback = (id) => http.post(`/commerce/recommendations/${id}/publish/rollback`).then((r) => r.data);

// ---- SpLink.pro proxy ----
export const splinkStatus = () => http.get("/splink/status").then((r) => r.data);
export const splinkCreate = (p) => http.post("/splink/links", p).then((r) => r.data);
export const splinkBulk = (urls) => http.post("/splink/bulk", { urls }).then((r) => r.data);
export const splinkList = (limit = 100) => http.get("/splink/links", { params: { limit } }).then((r) => r.data);
export const splinkStats = (slug) => http.get(`/splink/links/${slug}/stats`).then((r) => r.data);
export const splinkSummary = () => http.get("/splink/summary").then((r) => r.data);
export const splinkPublicShorten = (long_url, tenant_hint) => http.post("/splink/public/shorten", { long_url, tenant_hint }).then((r) => r.data);
export const splinkQrFor = (long_url) => http.get("/splink/qr-for", { params: { long_url } }).then((r) => r.data);
export const socialpulseStatus = () => http.get("/auth/socialpulse/status").then((r) => r.data);
export const socialpulseAuthorize = () => http.get("/auth/socialpulse/authorize").then((r) => r.data);

// ============================================================================
// Studio (AI Ad & Growth Studio)
// ============================================================================
export const studioPing = () => http.get("/studio/ping").then((r) => r.data);
export const studioMe = () => http.get("/studio/me").then((r) => r.data);
export const studioAdminFlag = (tenant_id, enabled) =>
  http.post("/studio/admin/flag", { tenant_id, enabled }).then((r) => r.data);
export const studioCreditsBalance = () => http.get("/studio/credits/balance").then((r) => r.data);
export const studioAuditRecent = (limit = 50) =>
  http.get("/studio/audit/recent", { params: { limit } }).then((r) => r.data);
// Onboarding
export const studioOnbQuestions = () => http.get("/studio/onboarding/questions").then((r) => r.data);
export const studioOnbState = () => http.get("/studio/onboarding/state").then((r) => r.data);
export const studioOnbAnswer = (question_id, value) =>
  http.post("/studio/onboarding/answer", { question_id, value }).then((r) => r.data);
export const studioOnbReset = () => http.post("/studio/onboarding/reset").then((r) => r.data);
export const studioOnbGenerateReadiness = () =>
  http.post("/studio/onboarding/generate-readiness").then((r) => r.data);
export const studioOnbReadiness = () => http.get("/studio/onboarding/readiness").then((r) => r.data);
// Brand DNA
export const studioBrandDnaCurrent = () => http.get("/studio/brand-dna/current").then((r) => r.data);
export const studioBrandDnaVersions = () => http.get("/studio/brand-dna/versions").then((r) => r.data);
export const studioBrandDnaCreate = (payload = {}) =>
  http.post("/studio/brand-dna", { payload }).then((r) => r.data);
export const studioBrandDnaUpdate = (version, payload = {}) =>
  http.put(`/studio/brand-dna/${version}`, { payload }).then((r) => r.data);
export const studioBrandDnaPublish = (version) =>
  http.post(`/studio/brand-dna/${version}/publish`).then((r) => r.data);
// Credits
export const studioCreditsTopup = (amount) =>
  http.post("/studio/credits/topup", { amount }).then((r) => r.data);
export const studioCreditsEstimate = (action, extra = {}) =>
  http.post("/studio/credits/estimate", { action, ...extra }).then((r) => r.data);
// Uploads
export const studioUploadLogo = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return http.post("/studio/uploads/logo", fd,
    { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const studioUploadsList = (kind) =>
  http.get("/studio/uploads", { params: kind ? { kind } : {} }).then((r) => r.data);
export const studioUploadDelete = (id) =>
  http.delete(`/studio/uploads/${id}`).then((r) => r.data);
// Products + Page-to-Campaign
export const studioProductsList = () => http.get("/studio/products").then((r) => r.data);
export const studioProductCreate = (payload) =>
  http.post("/studio/products", payload).then((r) => r.data);
export const studioProductImportUrl = (url, kind = "product") =>
  http.post("/studio/products/import-url", { url, kind }).then((r) => r.data);
export const studioProductDelete = (id) =>
  http.delete(`/studio/products/${id}`).then((r) => r.data);
// Copywriter
export const studioCopyFrameworks = () => http.get("/studio/copy/frameworks").then((r) => r.data);
export const studioCopyGenerate = (payload) =>
  http.post("/studio/copy/generate", payload).then((r) => r.data);
export const studioCopyVersions = (product_id) =>
  http.get("/studio/copy/versions", { params: product_id ? { product_id } : {} }).then((r) => r.data);
export const studioCopyLock = (id) => http.post(`/studio/copy/${id}/lock`).then((r) => r.data);
export const studioCopyUnlock = (id) => http.post(`/studio/copy/${id}/unlock`).then((r) => r.data);
// Static Ad Studio
export const studioAdGenerate = (payload) =>
  http.post("/studio/creative/static/generate", payload).then((r) => r.data);
export const studioAdAssets = (params = {}) =>
  http.get("/studio/creative/static/assets", { params }).then((r) => r.data);
// Landing Pages
export const studioLandingGenerate = (payload) =>
  http.post("/studio/landing-pages/generate", payload).then((r) => r.data);
export const studioLandingList = () => http.get("/studio/landing-pages").then((r) => r.data);
// Approvals
export const studioApprovalsList = (params = {}) =>
  http.get("/studio/approvals", { params }).then((r) => r.data);
export const studioApprovalsSummary = () =>
  http.get("/studio/approvals/summary").then((r) => r.data);
export const studioApprovalsDecide = (id, decision, reason) =>
  http.post(`/studio/approvals/${id}/decide`, { decision, reason }).then((r) => r.data);
// Bulk import
export const studioBulkImport = (urls, kind = "product") =>
  http.post("/studio/products/import-bulk", { urls, kind }).then((r) => r.data);
export const studioBulkImportCsv = (file, kind = "product") => {
  const fd = new FormData();
  fd.append("file", file);
  return http.post(`/studio/products/import-csv?kind=${encodeURIComponent(kind)}`, fd,
    { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const studioBulkJob = (id) =>
  http.get(`/studio/products/import-jobs/${id}`).then((r) => r.data);
export const studioBulkJobs = () =>
  http.get("/studio/products/import-jobs").then((r) => r.data);


// =========== Search Intelligence (Phase 1) ===========
export const siInstantAudit = (url) =>
  http.post("/si/audits/instant", { url }).then((r) => r.data);
export const siMethodology = () => http.get("/si/methodology").then((r) => r.data);
export const siSample = () => http.get("/si/sample").then((r) => r.data);
export const siListSites = () => http.get("/si/sites").then((r) => r.data);
export const siCreateSite = (url, label) =>
  http.post("/si/sites", { url, label }).then((r) => r.data);
export const siGetSite = (sid) => http.get(`/si/sites/${sid}`).then((r) => r.data);
export const siDeleteSite = (sid) => http.delete(`/si/sites/${sid}`).then((r) => r.data);
export const siSetContext = (sid, ctx) =>
  http.post(`/si/sites/${sid}/business-context`, ctx).then((r) => r.data);
export const siStartCrawl = (sid, cfg) =>
  http.post(`/si/sites/${sid}/crawls`, cfg).then((r) => r.data);
export const siListCrawls = (sid) =>
  http.get(`/si/sites/${sid}/crawls`).then((r) => r.data);
export const siGetCrawl = (sid, cid) =>
  http.get(`/si/sites/${sid}/crawls/${cid}`).then((r) => r.data);
export const siCancelCrawl = (sid, cid) =>
  http.delete(`/si/sites/${sid}/crawls/${cid}`).then((r) => r.data);
export const siCrawlPages = (sid, cid) =>
  http.get(`/si/sites/${sid}/crawls/${cid}/pages`).then((r) => r.data);
export const siCrawlIssues = (sid, cid) =>
  http.get(`/si/sites/${sid}/crawls/${cid}/issues`).then((r) => r.data);
export const siSiteScores = (sid) => http.get(`/si/sites/${sid}/scores`).then((r) => r.data);
export const siSeoAnalystAsk = (site_id, question) =>
  http.post("/si/assistants/seo-analyst/ask", { site_id, question }).then((r) => r.data);

// SI Report exports (auth via Bearer token appended manually because these
// return binary content).
export const siReportPdfUrl = (sid, cid, whiteLabel = false) =>
  `${API}/si/sites/${sid}/crawls/${cid}/report.pdf?white_label=${whiteLabel ? 1 : 0}`;
export const siReportCsvUrl = (sid, cid) =>
  `${API}/si/sites/${sid}/crawls/${cid}/report.csv`;
export const siDownloadReport = async (sid, cid, fmt = "pdf", whiteLabel = false) => {
  const url = fmt === "csv"
    ? siReportCsvUrl(sid, cid)
    : siReportPdfUrl(sid, cid, whiteLabel);
  const res = await http.get(url.replace(API, ""), { responseType: "blob" });
  const blob = new Blob([res.data], {
    type: fmt === "csv" ? "text/csv" : "application/pdf",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fmt === "csv"
    ? `si-findings-${cid.slice(0, 8)}.csv`
    : `si-report-${cid.slice(0, 8)}${whiteLabel ? "-white-label" : ""}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 4000);
};
export const siPublicSelfAudit = () =>
  http.get("/si/public/self-audit").then((r) => r.data);

// =========== Local Growth Hub ===========
export const lvRunAudit = (payload) =>
  http.post("/lv/audit", payload).then((r) => r.data);
export const lvGetAudit = (aid) =>
  http.get(`/lv/audit/${aid}`).then((r) => r.data);

export const inboxListThreads = (params = {}) =>
  http.get("/inbox/threads", { params }).then((r) => r.data);
export const inboxGetThread = (tid) =>
  http.get(`/inbox/threads/${tid}`).then((r) => r.data);
export const inboxReply = (tid, body, channel) =>
  http.post(`/inbox/threads/${tid}/reply`, { body, channel }).then((r) => r.data);
export const inboxSetStage = (tid, stage) =>
  http.put(`/inbox/threads/${tid}/stage`, { stage }).then((r) => r.data);

export const packagesPublic = () =>
  http.get("/packages").then((r) => r.data);
export const packagesAdminEdit = (pid, payload) =>
  http.put(`/admin/packages/${pid}`, payload).then((r) => r.data);

export const mmActions = () =>
  http.get("/mm/actions").then((r) => r.data);
export const mmApprove = (action_key) =>
  http.post("/mm/actions/approve", { action_key }).then((r) => r.data);

// =========== Identity + Onboarding + Reputation + Dashboard ===========
export const identityGet = () => http.get("/identity/me").then((r) => r.data);
export const identityPut = (payload) => http.put("/identity/me", payload).then((r) => r.data);
export const identityAddLocation = (payload) => http.post("/identity/locations", payload).then((r) => r.data);
export const identitySetSuccessManager = (payload) => http.put("/identity/success-manager", payload).then((r) => r.data);

export const onboardingComplete = (payload) =>
  http.post("/onboarding/complete", payload).then((r) => r.data);

export const reputationSendSurvey = (payload) =>
  http.post("/reputation/surveys", payload).then((r) => r.data);
export const reputationCaptureFeedback = (payload) =>
  http.post("/reputation/feedback", payload).then((r) => r.data);
export const reputationListFeedback = (sentiment) =>
  http.get("/reputation/feedback", { params: { sentiment } }).then((r) => r.data);
export const reputationOwnerReply = (fid, response_body, publish_as_testimonial) =>
  http.post(`/reputation/responses/${fid}`, { response_body, publish_as_testimonial }).then((r) => r.data);
export const reputationPublicTestimonials = (owner) =>
  http.get("/reputation/testimonials/public", { params: { owner } }).then((r) => r.data);

export const dashboardSummary = () =>
  http.get("/dashboard/summary").then((r) => r.data);

// Executive digest — weekly PDF summary
// Executive digest — weekly PDF summary
export const locationsRollup = () =>
  http.get("/locations/rollup").then((r) => r.data);
export const locationRollupDetail = (lid) =>
  http.get(`/locations/${lid}/rollup`).then((r) => r.data);
export const locationCreate = (payload) =>
  http.post("/locations", payload).then((r) => r.data);
export const locationPatch = (lid, payload) =>
  http.patch(`/locations/${lid}`, payload).then((r) => r.data);
export const locationDelete = (lid) =>
  http.delete(`/locations/${lid}`).then((r) => r.data);

// Missed-call text-back
export const callsSettings = () =>
  http.get("/calls/settings").then((r) => r.data);
export const callsSettingsSave = (payload) =>
  http.put("/calls/settings", payload).then((r) => r.data);
export const callsMissed = (limit = 50) =>
  http.get("/calls/missed", { params: { limit } }).then((r) => r.data);

// Email signature
export const emailSignatureGet = () =>
  http.get("/settings/email-signature").then((r) => r.data);
export const emailSignatureSave = (payload) =>
  http.put("/settings/email-signature", payload).then((r) => r.data);

// Invoice send-via-email
export const invoiceSend = (id) =>
  http.post(`/invoices/${id}/send`, {}).then((r) => r.data);

export const execDigestSettings = () =>
  http.get("/exec/digest/settings").then((r) => r.data);
export const execDigestSettingsSave = (payload) =>
  http.put("/exec/digest/settings", payload).then((r) => r.data);
export const execDigestPreview = () =>
  http.get("/exec/digest/preview").then((r) => r.data);
export const execDigestSend = (dry_run = false) =>
  http.post("/exec/digest/send", { dry_run }).then((r) => r.data);
export const execDigestDownloadPdf = async () => {
  const r = await http.get("/exec/digest.pdf", { responseType: "blob" });
  const cd = r.headers["content-disposition"] || "";
  const m = /filename="([^"]+)"/.exec(cd);
  const filename = m ? m[1] : "executive-digest.pdf";
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
  return { ok: true, filename };
};

// Contacts — canonical address book
export const contactsList = (params = {}) =>
  http.get("/contacts", { params }).then((r) => r.data);
export const contactsGet = (id) =>
  http.get(`/contacts/${id}`).then((r) => r.data);
export const contactsCreate = (payload) =>
  http.post("/contacts", payload).then((r) => r.data);
export const contactsPatch = (id, payload) =>
  http.patch(`/contacts/${id}`, payload).then((r) => r.data);
export const contactsDelete = (id) =>
  http.delete(`/contacts/${id}`).then((r) => r.data);
export const contactsMerge = (keep_id, drop_ids) =>
  http.post("/contacts/merge", { keep_id, drop_ids }).then((r) => r.data);
export const contactsBackfill = () =>
  http.post("/contacts/backfill").then((r) => r.data);
export const contactsExportCsv = async () => {
  const r = await http.get("/contacts.csv", { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url; a.download = "contacts.csv";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

// Inbox live wire + routing
export const inboxSettings = () =>
  http.get("/inbox/settings").then((r) => r.data);
export const inboxRoutingSettings = () =>
  http.get("/inbox/routing/settings").then((r) => r.data);
export const inboxRoutingSave = (payload) =>
  http.put("/inbox/routing/settings", payload).then((r) => r.data);
export const inboxRoutingClassify = (thread_id) =>
  http.post("/inbox/routing/classify", { thread_id }).then((r) => r.data);
export const inboxRoutingQueue = (role) =>
  http.get("/inbox/routing/queue", { params: role ? { role } : {} })
    .then((r) => r.data);

// Estimates + Invoices
export const estimatesList = (status) =>
  http.get("/estimates", { params: status ? { status } : {} })
    .then((r) => r.data);
export const estimateGet = (id) =>
  http.get(`/estimates/${id}`).then((r) => r.data);
export const estimateCreate = (payload) =>
  http.post("/estimates", payload).then((r) => r.data);
export const estimatePatch = (id, payload) =>
  http.patch(`/estimates/${id}`, payload).then((r) => r.data);
export const estimateAiDraft = (id, payload) =>
  http.post(`/estimates/${id}/ai-draft`, payload).then((r) => r.data);
export const estimateApprove = (id) =>
  http.post(`/estimates/${id}/approve`).then((r) => r.data);
export const estimateSend = (id) =>
  http.post(`/estimates/${id}/send`).then((r) => r.data);
export const estimateConvertToInvoice = (id, due_days = 14) =>
  http.post(`/estimates/${id}/convert-to-invoice`, { due_days })
    .then((r) => r.data);
export const estimatePdfDownload = async (id, number) => {
  const r = await http.get(`/estimates/${id}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url; a.download = `${number || "proposal"}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};

export const invoicesList = (status) =>
  http.get("/invoices", { params: status ? { status } : {} })
    .then((r) => r.data);
export const invoiceGet = (id) =>
  http.get(`/invoices/${id}`).then((r) => r.data);
export const invoicePdfDownload = async (id, number) => {
  const r = await http.get(`/invoices/${id}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url; a.download = `${number || "invoice"}.pdf`;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
};
export const invoicePaymentLink = (id) =>
  http.post(`/invoices/${id}/payment-link`).then((r) => r.data);
export const invoiceMarkPaid = (id, method = "manual", reference = null) =>
  http.post(`/invoices/${id}/mark-paid`, { method, reference })
    .then((r) => r.data);

// Branding
export const siGetBranding = (sid) =>
  http.get(`/si/sites/${sid}/branding`).then((r) => r.data);
export const siPutBranding = (sid, payload) =>
  http.put(`/si/sites/${sid}/branding`, payload).then((r) => r.data);
export const siBrandingLogoUrl = (sid) =>
  `${API}/si/sites/${sid}/branding/logo`;

// Share links
export const siShareReport = (sid, cid, payload) =>
  http.post(`/si/sites/${sid}/crawls/${cid}/share`, payload).then((r) => r.data);

// =========== Client Inventory Admin ===========
export const clientInventoryClients = () =>
  http.get("/client-inventory/clients").then((r) => r.data);
export const clientInventoryUrls = (key) =>
  http.get(`/client-inventory/${key}/urls`).then((r) => r.data);
export const clientInventoryCapture = (key, payload) =>
  http.post(`/client-inventory/${key}/capture`, payload).then((r) => r.data);
export const clientInventoryMap = (key, rowId, payload) =>
  http.post(`/client-inventory/${key}/urls/${rowId}/map`, payload).then((r) => r.data);
export const clientInventoryClear = (key) =>
  http.delete(`/client-inventory/${key}`).then((r) => r.data);
export const clientInventoryCsvUrl = (key) =>
  `${API}/client-inventory/${key}/urls.csv`;

// =========== Growth Operator (Phase 1) ===========
export const goTiers = () => http.get("/growth-operator/tiers").then(r => r.data);
export const goPacks = () => http.get("/growth-operator/industry-packs").then(r => r.data);
export const goPack = (slug) => http.get(`/growth-operator/industry-packs/${slug}`).then(r => r.data);
export const goAgents = () => http.get("/growth-operator/agents").then(r => r.data);
export const goCreateBlueprint = (payload) => http.post("/growth-operator/blueprint", payload).then(r => r.data);
export const goGetBlueprint = (bid) => http.get(`/growth-operator/blueprint/${bid}`).then(r => r.data);
export const goAdminList = (status) =>
  http.get("/growth-operator/admin/blueprints", { params: status ? { status } : {} }).then(r => r.data);
export const goAdminUpdate = (bid, payload) =>
  http.post(`/growth-operator/admin/blueprints/${bid}/status`, payload).then(r => r.data);
export const goAdminGet = (bid) => http.get(`/growth-operator/admin/blueprints/${bid}`).then(r => r.data);

// =========== Growth Operator Hub (task marketplace + digest) ===========
export const goCreateTask = (payload) => http.post("/growth-operator/tasks", payload).then(r => r.data);
export const goListTasks = (status) =>
  http.get("/growth-operator/tasks", { params: status ? { status } : {} }).then(r => r.data);
export const goGetTask = (tid) => http.get(`/growth-operator/tasks/${tid}`).then(r => r.data);
export const goTaskAction = (tid, payload) =>
  http.post(`/growth-operator/tasks/${tid}/action`, payload).then(r => r.data);
export const goAdminTasks = (status) =>
  http.get("/growth-operator/admin/tasks", { params: status ? { status } : {} }).then(r => r.data);
export const goDigestPreview = () =>
  http.get("/growth-operator/admin/digest/preview").then(r => r.data);
export const goDigestSend = (dry_run = false) =>
  http.post("/growth-operator/admin/digest/send", { dry_run }).then(r => r.data);
export const goDigestRecipients = () => http.get("/growth-operator/admin/digest/recipients").then(r => r.data);
export const goDigestOpt = (email, opt_in) =>
  http.post("/growth-operator/admin/digest/opt", { email, opt_in }).then(r => r.data);
export const goRegenPackHero = (slug, style_tweak = "") =>
  http.post(`/growth-operator/admin/industry-packs/${slug}/hero/regenerate`,
             style_tweak ? { style_tweak } : {}).then(r => r.data);
export const goAuditCsvUrl = (from, to, cols) => {
  const p = new URLSearchParams();
  if (from) p.set("from_", from);
  if (to) p.set("to", to);
  if (cols && cols.length) p.set("cols", cols.join(","));
  const qs = p.toString() ? `?${p.toString()}` : "";
  return `${API}/growth-operator/admin/tasks/audit.csv${qs}`;
};
export const goSetDigestTz = (email, tz) =>
  http.post("/growth-operator/admin/digest/timezone", { email, tz }).then(r => r.data);


// Prospect Reports (admin) — Business Prospecting capture from public tools
export const listProspects = (params = {}) =>
  http.get("/prospects", { params }).then((r) => r.data);
export const getProspect = (id) => http.get(`/prospects/${id}`).then((r) => r.data);
export const prospectStats = () => http.get("/prospects/stats").then((r) => r.data);
export const addProspectNote = (id, text) =>
  http.post(`/prospects/${id}/notes`, { text }).then((r) => r.data);
export const setProspectStatus = (id, status) =>
  http.put(`/prospects/${id}/status`, { status }).then((r) => r.data);
export const recrawlProspect = (id) =>
  http.post(`/prospects/${id}/recrawl`, {}).then((r) => r.data);
export const enrichProspect = (id, force = true) =>
  http.post(`/prospects/${id}/enrich`, { force }).then((r) => r.data);
export const enrichMissingProspects = (limit = 25) =>
  http.post(`/prospects/enrich-missing`, {}, { params: { limit } }).then((r) => r.data);
export const patchOutreachDraft = (id, patch) =>
  http.put(`/prospects/${id}/outreach`, patch).then((r) => r.data);
export const sendOutreach = (id) =>
  http.post(`/prospects/${id}/outreach/send`, { send_now: true }).then((r) => r.data);
export const prospectsCsvUrl = (params = {}) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) p.set(k, String(v)); });
  const qs = p.toString() ? `?${p.toString()}` : "";
  const token = getToken();
  const tok = token ? `${qs.includes("?") || !qs ? (qs ? "&" : "?") : "?"}auth=${encodeURIComponent(token)}` : "";
  // Prefer a bearer-header download via fetch; the caller uses the helper below.
  return `${API}/prospects.csv${qs}${tok}`;
};
export const downloadProspectsCsv = async (params = {}) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) p.set(k, String(v)); });
  const qs = p.toString() ? `?${p.toString()}` : "";
  const token = getToken();
  const resp = await fetch(`${API}/prospects.csv${qs}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) throw new Error("Export failed");
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `prospects-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

